import { readFile } from "node:fs/promises";
import path from "node:path";
import { logger } from "./log.js";

const commentMarker = "<!-- revuiw -->";
const log = logger.child("ci");

export interface PublishCommentOptions {
  cwd: string;
  artifactUrl?: string;
  token?: string;
  repository?: string;
  pullRequestNumber?: number;
}

interface GitHubComment {
  id: number;
  body?: string;
  user?: { type?: string };
}

export async function publishComment(options: PublishCommentOptions): Promise<void> {
  const token = options.token ?? process.env.GITHUB_TOKEN;
  const repository = options.repository ?? process.env.GITHUB_REPOSITORY;
  const pullRequestNumber = options.pullRequestNumber ?? readPositiveInteger(process.env.REVUIW_PR_NUMBER);

  if (!token) throw new Error("GITHUB_TOKEN is required to publish the review comment");
  if (!repository || !repository.includes("/")) throw new Error("GITHUB_REPOSITORY must be in owner/repository form");
  if (!pullRequestNumber) throw new Error("REVUIW_PR_NUMBER must identify the pull request");

  const report = await readReport(options.cwd);
  const body = buildCommentBody(report, options.artifactUrl);
  const endpoint = `https://api.github.com/repos/${repository}/issues/${pullRequestNumber}/comments`;
  log.info(`Publishing review comment to ${repository}#${pullRequestNumber}`);
  const comments = await githubRequest<GitHubComment[]>(`${endpoint}?per_page=100`, token);
  const existing = comments.find((comment) => comment.user?.type === "Bot" && comment.body?.includes(commentMarker));

  if (existing) {
    log.info(`Updating existing comment ${existing.id}`);
    await githubRequest(`https://api.github.com/repos/${repository}/issues/comments/${existing.id}`, token, "PATCH", { body });
  } else {
    log.info("Creating new comment");
    await githubRequest(endpoint, token, "POST", { body });
  }
  log.info("Review comment published");
}

export async function applyResult(cwd: string): Promise<number> {
  const value = (await readFile(path.join(cwd, ".revuiw", "exit-code"), "utf8")).trim();
  const exitCode = Number(value);
  if (!Number.isInteger(exitCode) || exitCode < 0 || exitCode > 255) {
    throw new Error(`Invalid review exit code: ${JSON.stringify(value)}`);
  }
  log.info(`Applying review result: exit code ${exitCode} (${exitCode === 0 ? "pass" : "fail"})`);
  return exitCode;
}

export function buildCommentBody(report: string, artifactUrl?: string): string {
  const sections = [commentMarker, report.trim()];
  if (artifactUrl) sections.push(`[Open screenshots and logs](${artifactUrl})`);
  return sections.join("\n\n");
}

async function readReport(cwd: string): Promise<string> {
  try {
    return await readFile(path.join(cwd, ".revuiw", "comment.md"), "utf8");
  } catch {
    return "## Autonomous UI review\n\n**Result:** Review infrastructure failed.\n\nThe runner did not produce a report.";
  }
}

async function githubRequest<T>(url: string, token: string, method = "GET", body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    headers: {
      Accept: "application/vnd.github+json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "User-Agent": "revuiw",
      "X-GitHub-Api-Version": "2022-11-28",
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`GitHub API ${method} ${url} failed (${response.status}): ${detail}`);
  }
  return response.status === 204 ? undefined as T : await response.json() as T;
}

function readPositiveInteger(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}
